import { QuestionType2UIConfig } from "../../../../../components/ui/question-type2-ui";
import { GeneralQuestionUIConfig, GeneralQuestionUIConfigOption } from "../../../../../cvp-webgames-core/Components/ui/react/core-general-question-ui";
import BaseScenarioPart from "../../../../../cvp-webgames-core/Scenario/BaseScenarioPart";
import { VideoSegmentItem, SingleVideoSegmentItem } from "../../../../../cvp-webgames-core/VideoSegment/VideoSegmentItem";
import { ScoringType } from "../../../../../misc/tools";
import { GameBaseScenario } from "../../../../GameBaseScenario";
import Scenario5BufferInMri, { scenario5BufferInMriAudioCaching, scenario5BufferInMriVideoSegments } from "../../../scenario5-buffer-in-mri";
import { BufferInMriComponentsStep1ComponentMainConfig, BufferInMriComponentsStep1ComponentPart1Config } from "../buffer-in-mri-components-step1";

export default class BufferInMriComponentsStep1Part1 extends BaseScenarioPart<GameBaseScenario> {
	private stepName = 'Step 2'
	private get config(): BufferInMriComponentsStep1ComponentPart1Config {
		return this.mainConfig.part1Config;
	}
	constructor(scenario: GameBaseScenario, private onDone: () => void, private id: string, private mainConfig: BufferInMriComponentsStep1ComponentMainConfig) {
		super(scenario);
	}

	public get partId(): string {
		return this.id;
	}

	public get videoUrl(): string[] {
		return [this.config.videoUrl];
	}

	public start() {
		this.section1();
	}

	public section1() {
		const context = this;
        this.mainConfig.scenario.setAreaForCurrentScenario(6);
		this.mainConfig.scenario.uiWrapper.displayMixedMediaUI({
			title: this.mainConfig.scenario.props.name,
			subtitle: this.stepName,
			htmlData: `
				<p class="default-text add-pad">For the substitution test, you approach other employees and inquire as to whether they would do the same 
				thing in similar circumstances.<br/><br/>
				Housekeeper #1: I have never received training, or at least I don’t 
				remember any training on how to clean the MRI area. The signs are not even in English. I see 
				why this event happened. It could have happened to any of us.<br/><br/>
				Housekeeper #2: This has never happened before. I did not know the 
				MRI area had specific cleaning guidelines.<br/><br/>
				Housekeeper #3: I think I clean the MRI area in the same way I clean 
				the other areas. I haven’t been told anything different, but I’m not sure. I have never had 
				an issue like this.</p>
			`,
			okButton: 'Continue',
			talkingVideoSrc: scenario5BufferInMriVideoSegments.getItemByKey('MixedMedia').src,
			talkingVideoChapterId: scenario5BufferInMriVideoSegments.getItemByKey('MixedMedia').chapterIds[0],
			onContinueClicked: () => {
				context.section2();
			}
		})
	}

	public section2() {
		const context = this;
		this.mainConfig.scenario.uiWrapper.displayQuestonType2UI({
			title: this.mainConfig.scenario.props.name,
			subtitle: (this.stepName + ' - Question 1'),
			audioUrl: scenario5BufferInMriAudioCaching.getItemByKey(scenario5BufferInMriAudioCaching.audioKeys.question3).src,
			isYesCorrect: true,
			correctFeedback: `
				<p class="default-text">Correct. All housekeepers interviewed stated that they did 
				not receive, or do not remember receiving instructions on how to clean the MRI area. 
				Therefore, it’s reasonable to assume that there is a systematic issue with training, 
				education and vigilance instilled in housekeeping staff when it comes to MRI safety.</p>
			`,
			wrongFeedback: `
				<p class="default-text">This is not the best option. The other housekeepers stated 
				that they would do the same thing in similar circumstances.</p>
			`,
			question: 'Given the information received from staff interviews, is it reasonable to conclude that a systems or cultural issue exists, and other employees in that work area would behave the same way in a similar situation?',
			onSubmitClicked: (isCorrect: boolean, feedback: string, selectedTitle: string, parentQuestion: QuestionType2UIConfig) => {
				const mentorInfo = scenario5BufferInMriVideoSegments.getItemByKey('Feedback');
				context.scenario.onChoiceSubmittedQuestionType2(isCorrect, feedback, selectedTitle, (this.stepName + ' - Question 1 '), parentQuestion, () => {
					context.section3();
				}, () => {
					context.section3();
				}, undefined, undefined, undefined, mentorInfo.src, mentorInfo.chapterIds[6], mentorInfo.chapterIds[7]);
			}
		}, 2);
	}

	public section3() {
		const context = this;
		this.mainConfig.scenario.uiWrapper.displayQuestonType1UI({
			audioUrl: scenario5BufferInMriAudioCaching.getItemByKey(scenario5BufferInMriAudioCaching.audioKeys.question4).src,
			title: this.mainConfig.scenario.props.name,
			subtitle: (this.stepName + ' - Question 2'),
			questionTitle: 'Based on the substitution test, the accountability for this action…',
			options: [
				{
					title: 'Lies completely with the employee.',
					description: 'Select and submit an option.',
					feedbackHtml: `
						<p class="default-text">Not the best option. The accountability for this action 
						rests solely with leadership as this is classified as human error. It is true 
						that there may be training that occurs and the employee is expected to fully 
						engage in that process; however, this event is a result of the existing culture 
						and systematic deficiencies in knowledge within the organization.</p>
					`,
					isCorrect: false,
					score: ScoringType.bad
				},
				{
					title: 'Is shared between the employee and organizational leadership.',
					description: 'Select and submit an option.',
					feedbackHtml: `
						<p class="default-text">This is not the best option. The accountability for this 
						action rests solely with leadership as this is classified as human error. It is 
						true that there may be training that occurs and the employee is expected to fully 
						engage in that process; however, this event is a result of the existing culture and 
						systematic deficiencies in knowledge within the organization.</p>
					`,
					isCorrect: false,
					score: ScoringType.bad
				},
				{
					title: 'Lies solely with the leadership.',
					description: 'Select and submit an option.',
					feedbackHtml: `
						<p class="default-text">Correct. There are system or cultural issues identified.</p>
					`,
					isCorrect: true,
					score: ScoringType.good
				}
			],
			
			onSubmitClicked: (allOptions: GeneralQuestionUIConfigOption[], selectedChoices: GeneralQuestionUIConfigOption[], parentQuestionConfig: GeneralQuestionUIConfig) => {
				const mentorInfo = scenario5BufferInMriVideoSegments.getItemByKey('Feedback');
				context.scenario.onChoiceSubmittedQuestionType1(selectedChoices[0], (this.stepName + ' - Question 2 '), parentQuestionConfig, () => {
					this.onDone();
				}, () => {
					this.onDone();
				}, undefined, undefined, undefined, mentorInfo.src, mentorInfo.chapterIds[8], mentorInfo.chapterIds[9], mentorInfo.chapterIds[10], 'Lies completely with the employee.');
			}
		}, 3);
	}
}